// node modules
import dayjs from 'dayjs';
// utils
import objectKeys from 'utils/helpers/objectKeys';
// store
import LocaleStore from './store';
// packs
import packs from './packs';

class LocaleService extends LocaleStore {
  packs = packs;

  translate = (key: TranslationKey, ...args: string[]): string => {
    const message: DictionaryValue = this.packs[this.lang][key];

    return this.getMessage(message, ...args);
  };

  translateForLocale = (
    key: TranslationKey,
    locale: Lang,
    ...args: string[]
  ): string => {
    const message: DictionaryValue = this.packs[locale][key];

    return this.getMessage(message, ...args);
  };

  private getMessage = (message: DictionaryValue, ...args: string[]) => {
    if (typeof message === 'function') {
      return message(...args);
    }

    if (typeof message === 'string') {
      return message;
    }

    return '';
  };

  initDayJsLocales = () => {
    objectKeys(this.packs).forEach(key => {
      dayjs.updateLocale(key, {
        relativeTime: this.getRelativeTimeConfigByLocale(key),
      });
    });
  };

  private getRelativeTimeConfigByLocale = (locale: Lang) => ({
    future: '%s',
    past: '%s',
    s: this.translateForLocale('NOW', locale),
    ss: this.translateForLocale('NOW', locale),
    m: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) =>
      (isFuture
        ? this.translateForLocale('ONE_MIN_FUTURE', locale)
        : this.translateForLocale(
            withoutSuffix ? 'ONE_MIN_PAST' : 'ONE_MIN_PAST_WITH_SUFFIX',
            locale,
          )),
    mm: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) =>
      (isFuture
        ? this.translateForLocale('FEW_MIN_FUTURE', locale, `${number}`)
        : this.translateForLocale(
            withoutSuffix ? 'FEW_MIN_PAST' : 'FEW_MIN_PAST_WITH_SUFFIX',
            locale,
            `${number}`,
          )),
    h: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) =>
      (isFuture
        ? this.translateForLocale('ONE_HOUR_FUTURE', locale)
        : this.translateForLocale(
            withoutSuffix ? 'ONE_HOUR_PAST' : 'ONE_HOUR_PAST_WITH_SUFFIX',
            locale,
          )),
    hh: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) =>
      (isFuture
        ? this.translateForLocale('FEW_HOURS_FUTURE', locale, `${number}`)
        : this.translateForLocale(
            withoutSuffix ? 'FEW_HOURS_PAST' : 'FEW_HOURS_PAST_WITH_SUFFIX',
            locale,
            `${number}`,
          )),
    d: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) =>
      (isFuture
        ? this.translateForLocale('ONE_DAY_FUTURE', locale)
        : this.translateForLocale(
            withoutSuffix ? 'ONE_DAY_PAST' : 'ONE_DAY_PAST_WITH_SUFFIX',
            locale,
          )),
    dd: (
      number: number,
      withoutSuffix: boolean,
      key: string,
      isFuture: boolean,
    ) => {
      if (number === 7) {
        return isFuture
          ? this.translateForLocale('ONE_WEEK_FUTURE', locale)
          : this.translateForLocale(
              withoutSuffix ? 'ONE_WEEK_PAST' : 'ONE_WEEK_PAST_WITH_SUFFIX',
              locale,
            );
      }

      return isFuture
        ? this.translateForLocale('FEW_DAYS_FUTURE', locale, `${number}`)
        : this.translateForLocale(
            withoutSuffix ? 'FEW_DAYS_PAST' : 'FEW_DAYS_PAST_WITH_SUFFIX',
            locale,
            `${number}`,
          );
    },
  });
}

export default new LocaleService();
